/**
 * What the weekly move cap held back from one store in one run — `docs/adr/6100`, bata-ui#166 W4-1.
 *
 * The answer to "why did this store get less than it needed" that the proposals cannot give: a
 * move the engine proposed and the store's loading bay had no week for is DEFERRED, not proposed,
 * so it is on no queue. The platform stores each one (bata-platform ADR 6100) and publishes two
 * reads, both bounded by the run in the URL:
 *
 *   - `GET /inseason/runs/{run_id}/move-cap-deferrals/summary` — per stage, whether the run's
 *     tick RECORDED its deferrals. A run older than the record says "not recorded", which is a
 *     different finding from "nothing held back", and this panel keeps them apart;
 *   - `GET /inseason/runs/{run_id}/move-cap-deferrals?store_key=` — the deferrals touching the
 *     store at either end: moves it would have received, and consolidations that would have
 *     taken stock from it as a donor. Read only once the summary says something was recorded,
 *     so the list's 409 ("not recorded") is never the way the panel learns it.
 *
 * The size-run repair stage is paired with the run BY TICK (bata-platform ADR 6100, amended at
 * review): its record is the repair execution of the run's own market and tick, never another
 * tick's of the same monthly window. The panel names that execution — when it was recorded and
 * how it was found (`resolved_by`) — so a planner can tell which execution they are reading; a
 * tick with no repair execution is "not recorded", and an untagged run's window-end fallback is
 * stated as such.
 *
 * Deferrals are at store × article × ship week, never at size: the move cap counts moves, not units.
 * So the article page shows the same panel narrowed to the article, not a mark on each size. Since
 * bata-reqs ADR 9000 the same record also holds size-run repair's drops held by the donor's weekly cap
 * on pairs sent (`outbound_units_cap_deferred`); on those rows `cap` is pairs a week.
 */

import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { listMoveCapDeferrals, moveCapDeferralSummary } from '@/api/generated';
import type { MoveCapDeferral, MoveCapDeferralSummary, MoveCapStage, MoveCapStageSummary } from '@/api/generated';
import { useApiCollection, useApiQuery } from '@/api';
import { formatDateTime } from '@/lib/format';
import { AsyncRegion, DataTable, KpiBand, useLocationSearch, type DataColumn } from '@/planning';
import { Badge, Measure, NumericCell, StatusIndicator } from '@/primitives';

import { articlePath } from './drill';
import { samePageTableHref } from './kpi-links';
import { NAME_HEADER_KEY, NameCell, nameColumn, useRunNames, type LineNames } from './run-names';
import { DrillLink } from './DrillTrail';
import { ABSENT_MEASURE, Panel } from './panels';

/** The reason code the cap writes. Any other is shown by its code, never guessed at. */
export const REASON_MOVE_CAP = 'move_cap_deferred';

/**
 * The units-sent cap's reason (bata-reqs ADR 9000, platform #888): size-run repair stores a drop its
 * donor had no pairs left to send under its own code in the same record, `bound_at` the donor and
 * `cap` that store's pairs a week (not moves).
 */
export const REASON_OUTBOUND_UNITS_CAP = 'outbound_units_cap_deferred';

/**
 * The destinations cap's reason (bata-platform ADR 9058): a move whose destination is not one of the
 * stores its origin's lane model opened this ship week, `bound_at` the origin and `cap` its stores a week.
 */
export const REASON_OUTBOUND_DESTINATIONS_CAP = 'outbound_destinations_cap_deferred';

/** A deferral's reason in words — the units-sent cap's naming the store whose cap held it. */
export function deferralReasonWords(
  code: string, translate: TFunction, boundAt: string | null = null,
): string {
  if (code === REASON_MOVE_CAP) return translate('inseason.moveCap.reason.capFull');
  if (code === REASON_OUTBOUND_UNITS_CAP) {
    return boundAt === null
      ? translate('inseason.moveCap.reason.unitsCapFull')
      : translate('inseason.moveCap.reason.unitsCapFullAt', { store: boundAt });
  }
  if (code === REASON_OUTBOUND_DESTINATIONS_CAP) {
    return boundAt === null
      ? translate('inseason.moveCap.reason.destinationsCapFull')
      : translate('inseason.moveCap.reason.destinationsCapFullAt', { store: boundAt });
  }
  return translate('inseason.moveCap.reason.other', { code });
}

export function stageLabelKey(stage: MoveCapStage | string): string {
  return stage === 'rebalance' ? 'inseason.moveCap.stage.rebalance' : 'inseason.moveCap.stage.distribute';
}

/** Whether any stage of the run's tick recorded its deferrals. */
export function anyRecorded(summary: MoveCapDeferralSummary): boolean {
  return summary.stages.some((stage) => stage.recorded);
}

/**
 * The store's figures over the rows read: moves it would have received and their units, and
 * consolidations that would have taken stock from it. Counted from the rows the platform
 * published, never estimated.
 */
/**
 * The deferrals table's name in the address bar and the id the figures scroll to (ADR 9069). «Received» counts the rows
 * where the store is the receiver and «As donor» those where it gives: the table's `role` column (`receives` / `gives`).
 */
const DEFERRALS_TABLE = 'move-cap';

export function deferralFigures(rows: readonly MoveCapDeferral[], storeKey: string): {
  readonly received: number;
  readonly receivedUnits: number;
  readonly asDonor: number;
} {
  const received = rows.filter((row) => row.store_key === storeKey);
  return {
    received: received.length,
    receivedUnits: received.reduce((sum, row) => sum + row.units, 0),
    asDonor: rows.filter((row) => row.donor_store_key === storeKey).length,
  };
}

/**
 * Which execution a stage's record is, as a caption key and its values — or `null` for the
 * distribute stage, which is the run's own. The rebalance stage names its execution by when it
 * was recorded and says how it was found; one not recorded says whether it was sought by tick.
 */
export function executionNote(
  stage: MoveCapStageSummary,
  formatInstant: (iso: string) => string,
): { readonly key: string; readonly values?: Readonly<Record<string, string>> } | null {
  if (stage.stage !== 'rebalance') return null;
  if (!stage.recorded) {
    return stage.resolved_by === 'tick' ? { key: 'inseason.moveCap.execution.noneInTick' } : null;
  }
  const values = { recordedAt: stage.recorded_at === null ? '' : formatInstant(stage.recorded_at) };
  if (stage.recorded_at === null) {
    return { key: stage.resolved_by === 'tick' ? 'inseason.moveCap.execution.tickUndated' : 'inseason.moveCap.execution.windowEndUndated' };
  }
  return { key: stage.resolved_by === 'tick' ? 'inseason.moveCap.execution.tick' : 'inseason.moveCap.execution.windowEnd', values };
}

function StageStatus({ stage }: { readonly stage: MoveCapStageSummary }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const name = t(stageLabelKey(stage.stage));
  const note = executionNote(stage, (iso) => formatDateTime(iso, i18n.language, 'UTC'));
  const caption = note === null ? null : (
    <span className="text-caption text-ink-secondary">{t(note.key, note.values ?? {})}</span>
  );
  if (!stage.recorded) {
    return (
      <div className="flex flex-col gap-1">
        <StatusIndicator level="warning" labelKey="inseason.moveCap.stageNotRecorded" values={{ stage: name }} />
        {caption}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <StatusIndicator
        level={stage.deferred_moves === 0 ? 'good' : 'warning'}
        labelKey="inseason.moveCap.stageRecorded"
        values={{ stage: name, moves: stage.deferred_moves ?? 0 }}
      />
      {caption}
    </div>
  );
}

function DeferralsTable({ rows, runId, storeKey, articleKey, lines }: {
  readonly rows: readonly MoveCapDeferral[];
  readonly runId: string;
  readonly storeKey: string;
  readonly articleKey: string | null;
  readonly lines: readonly LineNames[] | null | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  // One line per row, and constant text said once (client rule 2): a missing other door or ship
  // week is a dash in its cell, and what the dash means is a note under the table, once.
  const noOtherStore = rows.some((row) => (row.store_key === storeKey ? row.donor_store_key : row.store_key) === null);
  const noShipWeek = rows.some((row) => row.ship_week === null);
  // Article and other door by name beside the code (usability audit B3). The store's own proposal
  // lines name their keys (platform ADR 6080); the rest — an article the run proposed nothing of, the
  // other door — are asked of the run by key in one request (ADR 6177), never by walking a report
  // (audit 4); `null`: the lines are on the way.
  const names = useRunNames(
    lines === null ? null : runId,
    lines === undefined || lines === null ? { replenishment: true, transfers: true } : { replenishment: true, transfers: true, lines },
  );
  return (
    <div id={DEFERRALS_TABLE} className="flex flex-col gap-2">
    <DataTable sourceKey="inseason.help.moveCapDeferralsPanel.source" helpValues={{ run: runId, store: storeKey }}
      urlKey={DEFERRALS_TABLE}
      rows={rows}
      rowKey={(row) => row.deferral_id}
      captionKey={articleKey === null ? 'inseason.moveCap.caption' : 'inseason.moveCap.captionArticle'}
      {...(articleKey === null ? {} : { captionValues: { article: articleKey } })}
      captionHidden={false}
      exportId={articleKey === null ? 'inseason.store.moveCapDeferrals' : 'inseason.storeArticle.moveCapDeferrals'}
      exportTitleKey="inseason.moveCap.title"
      columns={([
        {
          id: 'article_key', headerKey: 'inseason.column.article', descriptionKey: 'inseason.help.moveCapDeferralsPanel.colArticle', kind: 'text',
          value: (row) => row.article_key,
          cell: (row) => (
            <th scope="row" className="py-1 text-start font-normal">
              {articleKey === null ? (
                <DrillLink
                  path={articlePath(storeKey, row.article_key)}
                  runId={runId}
                  labelKey="inseason.drill.openArticle"
                  labelValues={{ article: row.article_key }}
                >
                  {row.article_key}
                </DrillLink>
              ) : row.article_key}
            </th>
          ),
        },
        nameColumn<MoveCapDeferral>('article_name', NAME_HEADER_KEY.article, (row) => names.article(row.article_key), { descriptionKey: 'inseason.help.names.articleName' }),
        {
          id: 'stage', headerKey: 'inseason.moveCap.column.stage', descriptionKey: 'inseason.help.moveCapDeferralsPanel.colStage', kind: 'enum',
          value: (row) => row.stage, label: (stage) => t(stageLabelKey(stage)),
          cell: (row) => <td className="py-1"><Badge labelKey={stageLabelKey(row.stage)} /></td>,
        },
        {
          id: 'role', headerKey: 'inseason.moveCap.column.role', descriptionKey: 'inseason.help.moveCapDeferralsPanel.colRole', kind: 'enum',
          value: (row) => (row.store_key === storeKey ? 'receives' : 'gives'),
          label: (role) => t(role === 'receives' ? 'inseason.moveCap.role.receives' : 'inseason.moveCap.role.gives'),
          cell: (row) => (
            <td className="py-1">
              <Badge labelKey={row.store_key === storeKey ? 'inseason.moveCap.role.receives' : 'inseason.moveCap.role.gives'} />
            </td>
          ),
        },
        {
          id: 'other_store', headerKey: 'inseason.moveCap.column.otherStore', descriptionKey: 'inseason.help.moveCapDeferralsPanel.colOtherStore', kind: 'text',
          // The door at the other end of the move: the keeper for a consolidation this store
          // gives to, the donor for one it receives. A distribute drop has none — its donors are
          // pooled before allocation — and says so.
          value: (row) => (row.store_key === storeKey ? row.donor_store_key : row.store_key),
          cell: (row) => {
            const other = row.store_key === storeKey ? row.donor_store_key : row.store_key;
            return (
              <td className="py-1">
                {other ?? <span className="text-ink-secondary">{t('inseason.moveCap.noOtherStore')}</span>}
              </td>
            );
          },
        },
        {
          // The other door's name; a distribute drop has no other door, so no name either.
          id: 'other_store_name', headerKey: 'inseason.moveCap.column.otherStoreName', descriptionKey: 'inseason.help.moveCapDeferralsPanel.colOtherStoreName', kind: 'text',
          value: (row) => {
            const other = row.store_key === storeKey ? row.donor_store_key : row.store_key;
            return other === null ? null : names.store(other).name;
          },
          cell: (row) => {
            const other = row.store_key === storeKey ? row.donor_store_key : row.store_key;
            // No other door, no name: the same words as the door column's, which says why once.
            return other === null
              ? <td className="text-ink-secondary py-1">{t('inseason.moveCap.noOtherStore')}</td>
              : <NameCell named={names.store(other)} />;
          },
        },
        {
          id: 'ship_week', headerKey: 'inseason.column.shipWeek', descriptionKey: 'inseason.help.moveCapDeferralsPanel.colShipWeek', kind: 'text',
          value: (row) => row.ship_week,
          cell: (row) => (
            <td className="py-1">
              {row.ship_week ?? <span className="text-ink-secondary">{t('inseason.moveCap.noShipWeek')}</span>}
            </td>
          ),
        },
        {
          id: 'units', headerKey: 'inseason.moveCap.column.units', descriptionKey: 'inseason.help.moveCapDeferralsPanel.colUnits', kind: 'number', align: 'end',
          value: (row) => row.units, cell: (row) => <NumericCell value={row.units} absent={ABSENT_MEASURE} />,
        },
        {
          id: 'bound_at', headerKey: 'inseason.moveCap.column.boundAt', descriptionKey: 'inseason.help.moveCapDeferralsPanel.colBoundAt', kind: 'text',
          value: (row) => row.bound_at, cell: (row) => <td className="py-1">{row.bound_at}</td>,
        },
        {
          id: 'cap', headerKey: 'inseason.moveCap.column.cap', descriptionKey: 'inseason.help.moveCapDeferralsPanel.colCap', kind: 'number', align: 'end',
          value: (row) => row.cap, cell: (row) => <NumericCell value={row.cap} absent={ABSENT_MEASURE} />,
        },
        {
          id: 'proposed_moves', headerKey: 'inseason.moveCap.column.proposedMoves', descriptionKey: 'inseason.help.moveCapDeferralsPanel.colProposed', kind: 'number', align: 'end',
          value: (row) => row.proposed_moves,
          cell: (row) => <NumericCell value={row.proposed_moves} absent={ABSENT_MEASURE} />,
        },
        {
          id: 'reason_code', headerKey: 'inseason.moveCap.column.reason', descriptionKey: 'inseason.help.moveCapDeferralsPanel.colReason', kind: 'enum',
          value: (row) => row.reason_code,
          label: (code) => deferralReasonWords(code, t),
          cell: (row) => (
            <td className="py-1">
              {row.reason_code === REASON_MOVE_CAP
                ? <Badge labelKey="inseason.moveCap.reason.capFull" />
                : row.reason_code === REASON_OUTBOUND_UNITS_CAP
                  ? <Badge labelKey="inseason.moveCap.reason.unitsCapFullAt" values={{ store: row.bound_at }} />
                  : row.reason_code === REASON_OUTBOUND_DESTINATIONS_CAP
                    ? <Badge labelKey="inseason.moveCap.reason.destinationsCapFullAt" values={{ store: row.bound_at }} />
                    : deferralReasonWords(row.reason_code, t)}
            </td>
          ),
        },
      ] satisfies readonly DataColumn<MoveCapDeferral>[])
        // On the article page the article is the page's SCOPE, not a value that happens to repeat:
        // the caption names it and the column is not drawn, whatever the row count. Not
        // `constantHoist`, which would draw the column on a one-row page and state the article a
        // second time above a caption that already names it.
        .filter((column) => articleKey === null || (column.id !== 'article_key' && column.id !== 'article_name'))}
    />
    {noOtherStore ? <p className="text-caption text-ink-secondary">{t('inseason.moveCap.noOtherStoreNote')}</p> : null}
    {noShipWeek ? <p className="text-caption text-ink-secondary">{t('inseason.moveCap.noShipWeekNote')}</p> : null}
    </div>
  );
}

export function MoveCapDeferralsPanel({ runId, storeKey, articleKey = null, lines }: {
  readonly runId: string;
  readonly storeKey: string;
  readonly articleKey?: string | null;
  /** The proposal lines the page already holds for the store (and article): they name their own keys. `null`: still being read. */
  readonly lines?: readonly LineNames[] | null;
}): React.JSX.Element {
  const search = useLocationSearch();
  const summary = useApiQuery(moveCapDeferralSummary, { path: { run_id: runId } });
  const recorded = summary.data !== undefined && anyRecorded(summary.data);
  const deferrals = useApiCollection(
    listMoveCapDeferrals,
    {
      path: { run_id: runId },
      query: articleKey === null ? { store_key: storeKey } : { store_key: storeKey, article_key: articleKey },
    },
    { enabled: recorded },
  );

  return (
    <Panel
      titleKey="inseason.moveCap.title"
      aboutKey={articleKey === null ? 'inseason.moveCap.about' : 'inseason.moveCap.aboutArticle'}
      tourAnchor={articleKey === null ? 'store-move-cap' : 'store-article-move-cap'}
    >
      <AsyncRegion
        query={summary}
        errorTitleKey="inseason.moveCap.summaryFailed"
        emptyTitleKey="inseason.moveCap.notRecordedTitle"
        emptyBodyKey="inseason.moveCap.notRecordedBody"
        isEmpty={(data) => !anyRecorded(data)}
        loadingRows={2}
      >
        {(data) => (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-4">
              {data.stages.map((stage) => <StageStatus key={stage.stage} stage={stage} />)}
            </div>
            <AsyncRegion
              query={deferrals}
              errorTitleKey="inseason.moveCap.failed"
              emptyTitleKey="inseason.moveCap.noneTitle"
              emptyBodyKey={articleKey === null ? 'inseason.moveCap.noneBody' : 'inseason.moveCap.noneBodyArticle'}
              isEmpty={(rows) => rows.length === 0}
              loadingRows={3}
            >
              {(rows) => {
                const figures = deferralFigures(rows, storeKey);
                return (
                  <div className="flex flex-col gap-4">
                    <KpiBand>
                      <Measure descriptionKey="inseason.help.moveCapDeferralsPanel.kpiReceived" descriptionValues={{ store: storeKey }}
                        size="metric" labelKey="inseason.moveCap.kpi.received" unitKey="inseason.moveCap.unit.moves"
                        value={figures.received} absent={ABSENT_MEASURE}
                        href={samePageTableHref(search, DEFERRALS_TABLE, { role: ['receives'] })}
                      />
                      <Measure descriptionKey="inseason.help.moveCapDeferralsPanel.kpiReceivedUnits" descriptionValues={{ store: storeKey }}
                        size="metric" labelKey="inseason.moveCap.kpi.receivedUnits" unitKey="inseason.unit.pairs"
                        value={figures.receivedUnits} absent={ABSENT_MEASURE}
                      />
                      <Measure descriptionKey="inseason.help.moveCapDeferralsPanel.kpiAsDonor" descriptionValues={{ store: storeKey }}
                        size="metric" labelKey="inseason.moveCap.kpi.asDonor" unitKey="inseason.moveCap.unit.moves"
                        value={figures.asDonor} absent={ABSENT_MEASURE}
                        href={samePageTableHref(search, DEFERRALS_TABLE, { role: ['gives'] })}
                      />
                    </KpiBand>
                    <DeferralsTable rows={rows} runId={runId} storeKey={storeKey} articleKey={articleKey} lines={lines} />
                  </div>
                );
              }}
            </AsyncRegion>
          </div>
        )}
      </AsyncRegion>
    </Panel>
  );
}
